/**
 * Sending one photo of a receipt and getting lines back.
 *
 * Deliberately *not* built on `recogniseFood`. Two differences make sharing it
 * the wrong call:
 *
 * 1. **No caching.** The recognition cache is keyed by photo hash and exists
 *    because the same jar gets photographed twice. Nobody scans the same
 *    receipt twice, so the cache would only ever store a 400KB image hash for
 *    no benefit — and the cache is in localStorage, which is the one place a
 *    receipt must not end up.
 * 2. **A different reply shape.** A date and quantities, neither of which the
 *    food task has.
 *
 * What it *does* share is the part that matters: the same endpoint, the same
 * proxy, the same failure taxonomy. A receipt that fails because the account
 * is out of credits says exactly that, in the same words as everywhere else.
 */

import { readVisionConfig } from "../vision/config";
import { failureForReply, FAILURE_MESSAGES } from "../vision/recogniseFood";
import type { RecognitionFailure } from "../vision/recogniseFood";
import { mergeReceipts, parseReceipt } from "./parseReceipt";
import type { ReceiptRead } from "./types";

/** Longer than a shelf scan: a receipt is more tokens and a bigger image. */
const TIMEOUT_MS = 40_000;

/**
 * What to say when a receipt times out.
 *
 * The generic "took too long" was useless, and it was useless in the precise
 * way that matters: a timeout is the *symptom* of an oversized payload, not a
 * separate problem. A request the far end chokes on arrives as forty seconds of
 * silence, so the message has to carry the one number that makes the cause
 * visible.
 */
function timeoutMessage(photo: string): string {
  const kb = Math.round(photo.length / 1024);
  return (
    `The photo service didn't answer in 40 seconds (sent ${kb}KB). ` +
    `A large photo is the usual cause — try again with the receipt flat and ` +
    `filling the frame, or in two shorter photos.`
  );
}

export type ReceiptOutcome =
  | { status: "ok"; read: ReceiptRead }
  | { status: "off" }
  | { status: "failed"; reason: RecognitionFailure; message: string };

/** Reads one photo. Never throws. */
export async function readReceipt(photo: string, today: string): Promise<ReceiptOutcome> {
  const config = readVisionConfig();
  if (!config) return { status: "off" };

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TIMEOUT_MS);

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (config.apiKey) headers.Authorization = `Bearer ${config.apiKey}`;

    const response = await fetch(config.endpoint, {
      method: "POST",
      signal: controller.signal,
      headers,
      // An id, never prompt text. The receipt prompt, its token budget and its
      // size limit all live on the server.
      body: JSON.stringify({ photo, task: "receipt" }),
    });

    if (!response.ok) {
      // Same reason as the shelf path: our refusals and the upstream's collide
      // on status alone, so the body is what tells them apart.
      const problem: unknown = await response.json().catch(() => undefined);
      const reason = failureForReply(response.status, problem);
      // 413 is the one status where the size is the whole story, so say it.
      if (response.status === 413) {
        return {
          status: "failed",
          reason,
          message:
            `That photo was too large to send (${Math.round(photo.length / 1024)}KB). ` +
            `Try again with the receipt flat and filling the frame.`,
        };
      }
      return { status: "failed", reason, message: FAILURE_MESSAGES[reason] };
    }

    const body: unknown = await response.json();
    const text = (body as { choices?: { message?: { content?: unknown } }[] })?.choices?.[0]
      ?.message?.content;
    if (typeof text !== "string") {
      return { status: "failed", reason: "unreadable", message: FAILURE_MESSAGES.unreadable };
    }

    const read = parseReceipt(text, today);
    if (read.lines.length === 0) {
      // A receipt with no readable lines is the faded-thermal-print case, and
      // it needs its own sentence: "couldn't tell what that is" is written for
      // one jar and is no help at all when someone has just photographed a
      // whole shop.
      return {
        status: "failed",
        reason: "unreadable",
        message: "Couldn't read any items — try a straighter photo in better light.",
      };
    }

    return { status: "ok", read };
  } catch {
    if (timedOut) {
      return { status: "failed", reason: "timeout", message: timeoutMessage(photo) };
    }
    return { status: "failed", reason: "unreachable", message: FAILURE_MESSAGES.unreachable };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Reads several photos of one receipt, in sequence.
 *
 * Sequential rather than parallel on purpose: the limiter allows 30 photos an
 * hour, and firing four at once is a good way for a long receipt to spend a
 * quarter of the hour's budget and then be rate-limited halfway through. It
 * also means a failure part-way still returns what was read before it.
 */
export async function readReceiptPhotos(
  photos: string[],
  today: string
): Promise<ReceiptOutcome> {
  const reads: ReceiptRead[] = [];
  let lastFailure: ReceiptOutcome | null = null;

  for (const photo of photos) {
    const outcome = await readReceipt(photo, today);
    if (outcome.status === "ok") {
      reads.push(outcome.read);
      continue;
    }
    if (outcome.status === "off") return outcome;
    lastFailure = outcome;
  }

  // Something is better than nothing: three good photos and one blurry one
  // should still add the three photos' worth of shopping.
  if (reads.length > 0) return { status: "ok", read: mergeReceipts(reads) };
  return lastFailure ?? { status: "failed", reason: "unreadable", message: FAILURE_MESSAGES.unreadable };
}
